import { FunctionComponent as F, useState } from "react";
import { useSelector } from "react-redux";
import { IState } from "../../core/store/store";
import { Typography, Stack, TextField } from "@mui/material";
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import { postNewPostRefCommentVote, postUpdatedPostRefComment } from "../../core/http/ref";
import { validateComment } from "../../utils/validate";
import { formatDate } from "../../utils/string";
import TextTooltip from "../UI/Tooltip/TextTooltip";
import { ISpielComment } from "../../core/types/ref";


interface IProps
{
  key: number,
  comment: ISpielComment,
  handleRemove: (postRefCommentId: number) => void,
  handleEdit: (postRefCommentId: number, text: string) => void
}


const RefsTableRowComment: F<IProps> = (props: IProps) =>
{
  const user = useSelector((state: IState) => state.user);

  const [votes, setVotes] = useState(props.comment.votes);
  const [voted, setVoted] = useState(props.comment.voted);

  function handleVote()
  {
    postNewPostRefCommentVote(props.comment.commentId);
    setVotes(votes + 1 * (voted ? -1 : 1));
    setVoted(!voted);
  }

  const [editedText, setEditedText] = useState<string | null>(null);
  const [editedTextError, setEditedTextError] = useState(false);

  function handleTextChange(event: React.ChangeEvent<HTMLInputElement>)
  {
    setEditedText(event.target.value);
    setEditedTextError(!validateComment(event.target.value));
  }

  function handleCancel()
  {
    setEditedText(null);
  }

  function handleDone()
  {
    props.handleEdit(props.comment.commentId, editedText || "");
    setEditedText(null);
    postUpdatedPostRefComment(props.comment.commentId, editedText || "");
  }

  function handleKeyPress(event: React.KeyboardEvent<HTMLDivElement>)
  {
    if(event.key === 'Enter')
      handleDone();
    else if(event.key === 'Escape')
      handleCancel();

    return false;
  }

  const cancelDoneSpace = -0.5;

  return(
    <>

      {props.comment.userId !== user.userId ?

        <Stack>
          <Stack direction={'row'} spacing={1}>
            <Typography
              sx={{ mt: 'auto' }}
              color='lightGray'
              fontSize='10px'>
              {formatDate(props.comment.time)}
            </Typography>
            <Typography
              color='lightGray'
              fontWeight='bold'
              fontSize='12px'>
              {props.comment.username + ":"}
            </Typography>
          </Stack>
          <Stack direction={'row'}>
            <Typography sx={{ ml: 2 }} fontSize='14px'>
              {props.comment.text}
            </Typography>
            <ArrowDropUpIcon
              fontSize='small'
              sx={{ mt: 0.28, ml: 0.5, color: voted ? 'green' : 'gray' }}
              onClick={handleVote}/>
            <Typography fontSize='11px' sx={{ mt: 0.5, ml: -0.3, color: 'gray' }}>{votes}</Typography>
          </Stack>
        </Stack> :

        <Stack sx={{ ml: 'auto', width: 220 }}>
          <Typography
            sx={{ ml: 'auto' }}
            color='lightGray'
            fontSize='10px'>
            {formatDate(props.comment.time)}
          </Typography>
          <Stack direction={'row'} sx={{ ml: 'auto', mr: 2, mt: -0.4 }}>
            <Typography fontSize='11px' sx={{ mt: 0.5, ml: -0.3, color: 'gray' }}>{votes}</Typography>
            <TextTooltip tooltipPlacement='top' lines={["remove"]}>
              <RemoveCircleIcon
                sx={{ height: 12, width: 12, mt: 0.7, ml: 1, color: 'gray' }}
                onClick={() => props.handleRemove(props.comment.commentId)}/>
            </TextTooltip>
            {editedText ?
              <>
                <TextTooltip tooltipPlacement='top' lines={["cancel"]}>
                  <ClearIcon
                    sx={{
                      height: 15, mt: 0.5, mr: cancelDoneSpace,
                      color: 'gray' }}
                    onClick={handleCancel}/>
                </TextTooltip>
                <TextTooltip tooltipPlacement='top' lines={["done"]}>
                  <DoneIcon
                    sx={{
                      height: 15, width: 15,
                      textAlign: 'center', overflow: 'hidden',
                      mt: 0.5, mr: 0.4, ml: cancelDoneSpace,
                      color: 'gray' }}
                    onClick={handleDone}/>
                </TextTooltip>
              </> :
              <TextTooltip tooltipPlacement='top' lines={["edit"]}>
                <EditIcon
                  sx={{ height: 15, width: 15, mt: 0.5, color: 'gray' }}
                  onClick={() => setEditedText(props.comment.text)}/>
              </TextTooltip>}
            {editedText ?
              <TextField
                variant='standard'
                sx={{ color: editedTextError ? 'red' : 'black' }}
                inputProps={{ style: { fontSize: 14 } }}
                fullWidth
                size='small'
                value={editedText}
                onChange={handleTextChange}
                onKeyPress={handleKeyPress}
                error={editedTextError}/> :
              <Typography sx={{ ml: 0.5, mb: 0.5, textAlign: 'right', wordBreak: "break-word" }} fontSize='14px'>
                {props.comment.text}
              </Typography>}
          </Stack>

        </Stack>}

    </>);
};

export default RefsTableRowComment;
